import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { BudgetHeader, BudgetListItem } from 'types/api/budgets.type';

import { useAppSelector } from 'redux/hooks';

import { budgetDataService } from 'services/budget.service';

import { BudgetContext, BudgetHeaderSignalsContext, LayoutContext } from 'contexts';

import { useFetch } from 'hooks';

import { formatSeasonLabel } from 'helpers/formatSeason';

const useHeader = () => {
  const { register } = useForm();
  const { t } = useTranslation();

  const { id, gender } = useParams();
  const { state } = useLocation() as { state?: { name?: string; type?: string } };

  const [data, dataSet] = useState<BudgetHeader>();
  const [budgetName, budgetNameSet] = useState<string>();
  const [budgetType, budgetTypeSet] = useState<string>();

  const { zone, season, marketLine, warehouse } = useAppSelector((root) => root.settings);

  const { breadcrumbSet, breadcrumbSecondarySet } = useContext(LayoutContext);
  const { budgetStatusSet, budgetStatus, date, dateSet, endDateSet } =
    useContext(BudgetContext);
  const { headerRefreshTrigger, isQuantityUpdating } = useContext(BudgetHeaderSignalsContext);

  // Depend on the resolved type string, not on its sources: budgetType goes
  // '' -> 'TODO' after the list fetch even when the navigation state already
  // had the type, which used to rebuild params and fetch the header twice.
  const type = state?.type || budgetType || '';
  const params = useMemo(
    () => ({
      season,
      zone,
      seasonLine: marketLine,
      warehouse: warehouse.code,
      budgetNum: id!,
      genderCode: gender!,
      type,
    }),
    [season, zone, marketLine, warehouse.code, id, gender, type]
  );

  const [isLoading, isLoadingSet] = useState(false);

  useEffect(() => {
    // Without the type the backend can't resolve the budget (400) — wait for it.
    if (!id || !zone || !season || !marketLine || !type) return;

    let isActive = true;
    isLoadingSet(true);

    budgetDataService
      .getBudgetHeader(id!, zone, params)
      .then((res) => {
        if (isActive) {
          dataSet(res.data);
          budgetStatusSet(res.data.budgetStatus);
          dateSet(res.data.currBudgetDate);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        if (isActive) isLoadingSet(false);
      });

    // eslint-disable-next-line consistent-return
    return () => {
      isActive = false;
    };
  }, [id, zone, season, marketLine, warehouse.code, headerRefreshTrigger, params, type]);

  const fetchBudgetList = useCallback(
    () => budgetDataService.getAll(season, marketLine, zone),
    [season, marketLine, zone]
  );

  // One budget list fetch for end date, type and name (it used to be fetched
  // a second time for the name/type fallback)
  const { content: budgetList } = useFetch(
    season && marketLine && zone ? fetchBudgetList : undefined,
    [season, marketLine, zone]
  );

  useEffect(() => {
    dataSet(undefined);
  }, [id, zone, season, marketLine, warehouse.code]);

  // End date, and the name / type when they weren't passed via navigation state
  useEffect(() => {
    if (budgetList && id) {
      const budgetItem =
        budgetList.find(
          (item: BudgetListItem) =>
            String(item.num) === String(id) && (!gender || item.genderCode === gender)
        ) ?? budgetList.find((item: BudgetListItem) => item.num === Number(id));
      if (budgetItem?.endDate) {
        endDateSet(budgetItem.endDate);
      }
      if (budgetItem?.type && !budgetType) {
        budgetTypeSet(budgetItem.type);
      }
      if (budgetItem?.name && !state?.name && !budgetName) {
        budgetNameSet(budgetItem.name);
      }
    }
  }, [budgetList, id, gender, endDateSet, budgetType, state?.name, budgetName]);

  useEffect(() => {
    if (id && date && zone) {
      if (marketLine) {
        const status = budgetStatus?.toLowerCase();
        let statusLabel = status && status !== 'null' ? budgetStatus : '';
        if (status === 'sent') statusLabel = t('status.sent');
        if (status === 'to send') statusLabel = t('status.toSend');

        const suffix = state?.name || budgetName || statusLabel;
        const breadcrumb = [
          {
            label: `BUDGET ${marketLine}`,
          },
          {
            label: zone,
          },
          {
            label: `N. ${id} - ${date}${suffix ? ` ${suffix}` : ''}`,
          },
        ];

        breadcrumbSet(breadcrumb);
      }

      if (season) {
        const breadcrumbSecondary = [
          {
            label: formatSeasonLabel(season),
          },
          {
            label: 'budget',
          },
          {
            label: zone,
          },
          {
            label: `N. ${id} - ${date}`,
          },
        ];

        breadcrumbSecondarySet(breadcrumbSecondary);
      }
    }
  }, [season, marketLine, zone, id, budgetStatus, date, state, budgetName, t]);

  return {
    isLoading,
    data,
    register,
    isQuantityUpdating,
  };
};

export { useHeader };
